import { apiClient } from "./client";
import { useQuery, useMutation as useTQMutation, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "react-query";

export const useUsers = (
  token: string,
  page: number,
  pageSize: number,
  role: string
) => {
  return useQuery({
    queryKey: ["users", role, page, pageSize],
    queryFn: async () => {
      const res = await apiClient.get(
        `admin/read-all-users?role=${role}&page=${page}&pageSize=${pageSize}`
      );
      return res.data;
    },
    staleTime: 600000,
    enabled: !!token,
  });
};

export const useUsersNumber = () => {
  return useQuery({
    queryKey: ["usersNumber"],
    queryFn: async () => {
      const res = await apiClient.get(`admin/read-all-users-number`);
      return res.data;
    },
    staleTime: 600000,
  });
};

export const searchUsers = async (token: string, searchValue: string) => {
  const res = await apiClient.get(
    `admin/search-users?term=${searchValue}`
  );
  return res.data;
};

export const updateUser = async (
  userData: any,
  userId: string,
  accessToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `user/update/${userId}`,
      userData,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

export const getUser = async (
  userId: string,
  accessToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(`user/read/${userId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error getting user:", error);
    throw error;
  }
};

// ── Admin CRUD operations (apiClient interceptor attaches the token) ──

export const editUser = async (
  userId: string,
  updates: { firstName?: string; lastName?: string; phone?: string; role?: string }
): Promise<void> => {
  await apiClient.patch(`admin/user/${userId}`, updates);
};

export const resetUserPassword = async (userId: string): Promise<{ sent: boolean }> => {
  const res = await apiClient.post(`admin/user/${userId}/reset-password`);
  return res.data;
};

export const disableUser = async (userId: string): Promise<void> => {
  await apiClient.post(`admin/user/${userId}/disable`);
};

export const enableUser = async (userId: string): Promise<void> => {
  await apiClient.post(`admin/user/${userId}/enable`);
};

export const deleteUser = async (
  userId: string,
  confirmEmail: string
): Promise<void> => {
  await apiClient.delete(`admin/user/${userId}`, { data: { confirmEmail } });
};

export const createUser = async (payload: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
  role: "client" | "freelancer" | "business" | "admin";
}): Promise<{ userId: string }> => {
  const res = await apiClient.post(`admin/user`, payload);
  return res.data;
};

export const useWallet = (userId: string) => {
  return useQuery({
    queryKey: ["adminWallet", userId],
    queryFn: async () => {
      if (!userId) {
        return [];
      }
      const res = await apiClient.get(`admin/wallet-read/${userId}`);
      return res.data;
    },
    staleTime: 600000,
    enabled: !!userId,
  });
};

export interface TvaResetRecord {
  id: string;
  resetAt: string;
  resetBy: string;
  amount: number;
}

export const useTvaHistory = () => {
  return useQuery<TvaResetRecord[]>({
    queryKey: ["adminTvaHistory"],
    queryFn: async () => {
      const res = await apiClient.get(`admin/tva-history`);
      return res.data;
    },
    staleTime: 60000,
  });
};

export const useResetTva = () => {
  const qc = useQueryClient();
  return useTQMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`admin/tva-reset`, {});
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adminWallet"] });
      qc.invalidateQueries({ queryKey: ["adminTvaHistory"] });
    },
  });
};

// Allowlist + size guard for admin avatar upload. Mirrors the web helper
// rules (image/jpeg|png|webp|gif, 5 MB). Centralization across the two apps
// is tracked under P08c.
const ADMIN_AVATAR_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ADMIN_AVATAR_MAX_SIZE = 5 * 1024 * 1024;

export const uploadPhoto = async (userId: string, file: File, token: string) => {
  if (!userId || !file) throw new Error("Missing userId or file");
  if (!token) throw new Error("Missing auth token for uploadPhoto");
  if (!ADMIN_AVATAR_MIME.includes(file.type)) {
    throw new Error(
      `Avatar must be JPEG/PNG/WebP/GIF; got "${file.type || "unknown"}".`
    );
  }
  if (file.size > ADMIN_AVATAR_MAX_SIZE) {
    throw new Error(
      `Avatar exceeds 5 MB limit (${(file.size / (1024 * 1024)).toFixed(1)} MB).`
    );
  }

  const formData = new FormData();
  formData.append("file", file);

  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const res = await apiClient.post(
      `user/upload-photo/${userId}`,
      formData,
      config
    );
    return res.data;
  } catch (error) {
    console.error("Error uploading photo:", error);
    throw error;
  }
};
